import React, {
  createContext,
  useReducer,
  useContext,
  ReactNode,
  useState,
  useMemo,
} from "react";
import { useLocation } from "react-router-dom";
import { QueryContact } from "@/api/queries/types";
import { useGetSavedQuery } from "@/api/queries/useGetSavedQuery";
import { Contact } from "@/api/types";
import { DocProducerDialog } from "@/components/DocProducer/DocProducerDialog";
import { DocProducerDocument } from "@/views/docproducer";

export interface ContactEntry {
  id: number;
  contactId: string;
  contact: Contact;
  documents: { documents: DocProducerDocument[]; [key: string]: object };
  modData: object;
  docProducerOverrides?: DocProducerOverrides;
}

export interface DocProducerOverrides {
  profile: Record<string, string>;
  sourceDocIds: string[];
  afterSign: Record<string, unknown> | null;
  docProdOut?: Record<string, unknown> | null;
}

type State = {
  contacts: ContactEntry[];
};

type Action =
  | { type: "ADD_CONTACT"; payload: ContactEntry }
  | {
      type: "IMPORT_DOCUMENTS";
      payload: {
        contactId: string;
        documents: DocProducerDocument[];
        modData: object;
        overrides: DocProducerOverrides;
      };
    }
  | { type: "REMOVE_CONTACT"; payload: number }
  | { type: "ADD_MOD_DATA"; payload: { contactId: string; data: object } }
  | { type: "CLEAR_MOD_DATA"; payload: { contactId: string } }
  | {
      type: "SET_DOC_PRODUCER_OVERRIDES";
      payload: {
        contactId: string;
        overrides: DocProducerOverrides;
      };
    }
  | {
      type: "REPLACE_DOCUMENTS";
      payload: {
        contactId: string;
        documents: { documents: DocProducerDocument[]; [key: string]: object };
      };
    }
  | {
      type: "ADD_DOCUMENT";
      payload: { contactId: string; document: DocProducerDocument };
    }
  | {
      type: "REORDER_DOCUMENTS";
      payload: { contactId: string; documents: DocProducerDocument[] };
    }
  | { type: "REMOVE_DOCUMENT"; payload: { contactId: string; taskId: string } };

interface DocProducerData {
  formKey: string;
  title: string;
  toProduction: boolean;
}

export interface TaskAttachment {
  taskId: string;
  name: string;
}

type DocProducerUIState = {
  isOpen: boolean;
  openDialog: (contact?: Contact | QueryContact) => void;
  closeDialog: () => void;
  openContact?: Contact | QueryContact | null;
  shouldRestore?: boolean;
};

const DocProducerContext = createContext<
  { state: State; dispatch: React.Dispatch<Action> } | undefined
>(undefined);

const DocProducerUIContext = createContext<DocProducerUIState | undefined>(
  undefined
);

function docProducerReducer(state: State, action: Action): State {
  switch (action.type) {
    case "IMPORT_DOCUMENTS":
      return {
        contacts: state.contacts.map((contact) =>
          contact.contactId === action.payload.contactId
            ? {
                ...contact,
                documents: {
                  contactData: contact.contact,
                  documents: action.payload.documents,
                },
                modData: action.payload.modData,
                docProducerOverrides: action.payload.overrides,
              }
            : contact
        ),
      };
    case "ADD_CONTACT":
      if (
        state.contacts.some((c) => c.contactId === action.payload.contactId)
      ) {
        return state; // Already exists, do nothing
      }
      return {
        contacts: [
          ...state.contacts,
          {
            ...action.payload,
            contact: {
              ...action.payload.contact,
              portfolios: action.payload.contact.portfolios?.filter(
                (p) => p.status === "A"
              ),
            },
          },
        ],
      };

    case "REPLACE_DOCUMENTS":
      return {
        contacts: state.contacts.map((c) =>
          c.contactId === action.payload.contactId
            ? {
                ...c,
                documents: action.payload.documents,
              }
            : c
        ),
      };

    case "REMOVE_CONTACT":
      return {
        contacts: state.contacts.filter((c) => c.id !== action.payload),
      };

    case "ADD_MOD_DATA":
      return {
        contacts: state.contacts.map((c) =>
          c.contactId === action.payload.contactId
            ? {
                ...c,
                modData: action.payload.data,
              }
            : c
        ),
      };

    case "CLEAR_MOD_DATA":
      return {
        contacts: state.contacts.map((c) =>
          c.contactId === action.payload.contactId
            ? {
                ...c,
                modData: {},
              }
            : c
        ),
      };
    case "SET_DOC_PRODUCER_OVERRIDES":
      return {
        contacts: state.contacts.map((c) =>
          c.contactId === action.payload.contactId
            ? { ...c, docProducerOverrides: action.payload.overrides }
            : c
        ),
      };
    case "ADD_DOCUMENT":
      return {
        contacts: state.contacts.map((c) =>
          c.contactId === action.payload.contactId
            ? {
                ...c,
                documents: {
                  ...c.documents,
                  documents: [
                    ...c.documents.documents.filter(
                      (doc) => doc.id !== action.payload.document.id
                    ),
                    action.payload.document,
                  ],
                },
              }
            : c
        ),
      };

    case "REMOVE_DOCUMENT":
      return {
        contacts: state.contacts.map((c) =>
          c.contactId === action.payload.contactId
            ? {
                ...c,
                documents: {
                  ...c.documents,
                  documents: c.documents.documents.filter(
                    (doc) => doc.id !== action.payload.taskId
                  ),
                },
              }
            : c
        ),
      };
    case "REORDER_DOCUMENTS":
      return {
        contacts: state.contacts.map((c) =>
          c.contactId === action.payload.contactId
            ? {
                ...c,
                documents: {
                  ...c.documents,
                  documents: action.payload.documents,
                },
              }
            : c
        ),
      };

    default:
      return state;
  }
}

export function DocProducerProvider({ children }: { children: ReactNode }) {
  const location = useLocation();
  const [state, dispatch] = useReducer(docProducerReducer, { contacts: [] });
  const [open, setOpen] = useState(false);
  const [contacts, setContacts] = useState<QueryContact[]>([]);
  const { fetchQueryResults } = useGetSavedQuery();
  const [openContact, setOpenContact] = useState<Contact | QueryContact | null>(
    null
  );
  const [shouldRestore, setShouldRestore] = useState(false);

  // Extract contactId from pathname (e.g., /contacts/123)
  const contactIdFromUrl = useMemo(() => {
    const match = location.pathname.match(/\/contacts\/(\d+)/);
    return match ? match[1] : null;
  }, [location.pathname]);

  const handleOpen = async (contact?: Contact | QueryContact) => {
    if (open) {
      // If already open, trigger restore from minimized
      setShouldRestore(true);
    } else {
      setOpen(true);
    }
    await fetchQueryResults<QueryContact>("advPortalContactsSimple", {
      contactName: "%",
    }).then((results) => {
      setContacts(results.data?.savedQuery.queryResult.queryResultJson || []);
    });
    if (contact) {
      setOpenContact(contact);
    }
  };

  return (
    <DocProducerContext.Provider value={{ state, dispatch }}>
      <DocProducerUIContext.Provider
        value={{
          isOpen: open,
          openDialog: handleOpen,
          openContact: openContact || null,
          closeDialog: () => setOpen(false),
          shouldRestore,
        }}
      >
        {children}
        <DocProducerDialog
          isOpen={open}
          onClose={() => setOpen(false)}
          contacts={contacts}
          openContact={openContact}
          shouldRestore={shouldRestore}
          onRestoreComplete={() => setShouldRestore(false)}
          contactIdFromUrl={contactIdFromUrl}
        />
      </DocProducerUIContext.Provider>
    </DocProducerContext.Provider>
  );
}

export function useDocProducer() {
  const context = useContext(DocProducerContext);
  if (!context)
    throw new Error("useDocProducer must be used inside DocProducerProvider");
  return context;
}

export function useDocProducerUI() {
  const context = useContext(DocProducerUIContext);
  if (!context)
    throw new Error("useDocProducerUI must be used inside DocProducerProvider");
  return context;
}
